import { CancelButton, FormButton } from "../../../components/Button";
import useEdit from "./useEdit";
import Loader from "../../../components/Loader";
import { useForm } from "react-hook-form";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";
import { FormFieldset } from "../../../components/Form";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";

const EditHiring = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, onSubmit, uploadResume, upload } = useEdit();
  const { register, handleSubmit, setValue, watch } = useForm();
  const [originalNotes, setOriginalNotes] = useState<{ hrNote?: string; raviNote?: string }>({});
  
  // Build return URL with current form values (for Cancel button)
  const buildReturnURLWithCurrentValues = () => {
    const currentParams = new URLSearchParams(location.search);
    const returnParams = new URLSearchParams();
    
    console.log("=== CANCEL BUTTON - BUILDING RETURN URL ===");
    console.log("Current location.search:", location.search);
    console.log("Current form values:", {
      noticePeriod: watch("noticePeriod"),
      status: watch("status"),
      experienceTech: watch("experienceTech")
    });
    
    // Start with page from URL or default to 1
    const page = currentParams.get("page") || "1";
    returnParams.set("page", page);
    
    // Use current form values for filters
    const currentNoticePeriod = watch("noticePeriod");
    const currentStatus = watch("status");
    const currentTechnology = watch("experienceTech");
    
    if (currentNoticePeriod) {
      returnParams.set("noticePeriod", currentNoticePeriod);
    } else if (currentParams.get("noticePeriod")) {
      returnParams.set("noticePeriod", currentParams.get("noticePeriod")!);
    }
    
    if (currentStatus) {
      returnParams.set("status", currentStatus);
    } else if (currentParams.get("status")) {
      returnParams.set("status", currentParams.get("status")!);
    }
    
    if (currentTechnology) {
      returnParams.set("technology", currentTechnology);
    } else if (currentParams.get("technology")) {
      returnParams.set("technology", currentParams.get("technology")!);
    }
    
    // Keep search if it exists
    if (currentParams.get("search")) {
      returnParams.set("search", currentParams.get("search")!);
    }
    
    const returnURL = `/hiring?${returnParams.toString()}`;
    console.log("Built return URL:", returnURL);
    return returnURL;
  };

  useEffect(() => {
    const fetchHiring = async () => {
      const formData = new FormData();

      console.log("Upload resume ========", upload);

      if (upload) {
        formData.append("resume", upload);
      }
      if (!id) return;
      try {
        const result = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.getHiring}${id}`,
        });
        const hiring = result.hiring;
        if (hiring) {
          // Store original note values to compare later
          setOriginalNotes({
            hrNote: hiring.hrNote || "",
            raviNote: hiring.raviNote || "",
          });
          
          console.log("=== FETCHED HIRING DATA ===");
          console.log("Notice Period:", hiring.noticePeriod);
          console.log("Status:", hiring.status);
          console.log("Technology:", hiring.experienceTech);
          
          Object.keys(hiring).forEach((key) => {
            setValue(key, hiring[key]);
          });
        }
      } catch (error) {
        console.error("Error fetching hiring:", error);
      }
    };
    fetchHiring();
  }, [id, setValue]);

  return (
    <div className="mx-auto bg-white p-8 rounded-xl shadow-lg mt-8 max-h-[80vh] overflow-y-auto">
      <div className="flex flex-col items-center w-full h-full">
        <div className="w-full">
          <div className="border-b-2 w-full mb-6">
            <h1 className="text-3xl mb-2 text-fontBlack ml-2 font-bold">
              Edit Hiring
            </h1>
          </div>
          {loading && <Loader />}
          <form
            onSubmit={handleSubmit((data) => {
              console.log("=== FORM SUBMIT ===");
              console.log("Full form data:", data);
              console.log("Filter fields:");
              console.log("  noticePeriod:", data.noticePeriod);
              console.log("  status:", data.status);
              console.log("  experienceTech:", data.experienceTech);
              
              // Remove notes from data if they haven't changed to prevent duplication
              const submitData = { ...data };
              
              // Normalize note values for comparison (handle null, undefined, empty string)
              const normalizeNote = (note: any) => (note || "").toString().trim();
              
              // Only include hrNote if it has changed
              const currentHrNote = normalizeNote(submitData.hrNote);
              const originalHrNote = normalizeNote(originalNotes.hrNote);
              if (currentHrNote === originalHrNote) {
                delete submitData.hrNote;
              }
              
              // Only include raviNote if it has changed
              const currentRaviNote = normalizeNote(submitData.raviNote);
              const originalRaviNote = normalizeNote(originalNotes.raviNote);
              if (currentRaviNote === originalRaviNote) {
                delete submitData.raviNote;
              }
              
              console.log("Calling onSubmit with data:", submitData);
              onSubmit(id || "", submitData);
            })}
            className="space-y-6"
          >
            <fieldset className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <legend className="text-lg font-semibold text-gray-700 mb-4">
                Hiring Details
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="name"
                  >
                    Name
                  </label>
                  <input
                    id="name"
                    {...register("name")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("name") || ""}
                    onChange={(e) => setValue("name", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="phone"
                  >
                    Phone
                  </label>
                  <input
                    id="phone"
                    {...register("phone")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("phone") || ""}
                    onChange={(e) => setValue("phone", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="email"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    {...register("email")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("email") || ""}
                    onChange={(e) => setValue("email", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="education"
                  >
                    Education
                  </label>
                  <input
                    id="education"
                    {...register("education")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("education") || ""}
                    onChange={(e) => setValue("education", e.target.value)}
                  />
                </div>
                <div className="mb-4 flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    English
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("english") || ""}
                    onChange={(e) => setValue("english", e.target.value)}
                  >
                    <option value="">Select English Level</option>
                    <option value="Poor">Poor</option>
                    <option value="Good">Good</option>
                    <option value="Fluent">Fluent</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="experience"
                  >
                    Experience
                  </label>
                  <input
                    id="experience"
                    {...register("experience")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("experience") || ""}
                    onChange={(e) => setValue("experience", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="experienceTech"
                  >
                    Experience Tech
                  </label>
                  <input
                    id="experienceTech"
                    {...register("experienceTech")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("experienceTech") || ""}
                    onChange={(e) => setValue("experienceTech", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="hrNote"
                  >
                    HR Note
                  </label>
                  <input
                    id="hrNote"
                    {...register("hrNote")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("hrNote") || ""}
                    onChange={(e) => setValue("hrNote", e.target.value)}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-fontBlack text-sm mb-2">Type</label>
                  <select
                    value={watch("type") || ""}
                    onChange={e => setValue("type", e.target.value)}
                    className="border rounded p-2"
                  >
                    <option value="">Select Type</option>
                    <option value="Fresher">Fresher</option>
                    <option value="Experience">Experience</option>
                  </select>
                </div>
                <div className="mb-4 flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("status") || ""}
                    onChange={(e) => setValue("status", e.target.value)}
                  >
                    <option value="">Select Status</option>
                    <option value="Follow-Up">Follow-Up</option>
                    <option value="Hold">Hold</option>
                    <option value="Schedule">Schedule</option>
                    <option value="Rejection">Rejection</option>
                    <option value="High Salary">High Salary</option>
                    <option value="HR Rejection">HR Rejection</option>
                    <option value="HR Action">HR Action</option>
                    <option value="TL Action">TL Action</option>
                    <option value="Call NA">Call NA</option>
                    <option value="Practical Needed">Practical Needed</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="currentCTC"
                  >
                    Current CTC
                  </label>
                  <input
                    id="currentCTC"
                    {...register("currentCTC")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("currentCTC") || ""}
                    onChange={(e) => setValue("currentCTC", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="expectedCTC"
                  >
                    Expected CTC
                  </label>
                  <input
                    id="expectedCTC"
                    {...register("expectedCTC")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("expectedCTC") || ""}
                    onChange={(e) => setValue("expectedCTC", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="noticePeriod"
                  >
                    Notice Period
                  </label>
                  <input
                    id="noticePeriod"
                    {...register("noticePeriod")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("noticePeriod") || ""}
                    onChange={(e) => setValue("noticePeriod", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="source"
                  >
                    Source
                  </label>
                  <input
                    id="source"
                    {...register("source")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("source") || ""}
                    onChange={(e) => setValue("source", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="linkedInId"
                  >
                    LinkedIn Id
                  </label>
                  <input
                    id="linkedInId"
                    {...register("linkedInId")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("linkedInId") || ""}
                    onChange={(e) => setValue("linkedInId", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="location"
                  >
                    Location
                  </label>
                  <input
                    id="location"
                    {...register("location")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("location") || ""}
                    onChange={(e) => setValue("location", e.target.value)}
                  />
                </div>
                <div className="mb-4 flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Resume
                  </label>
                  <input
                    type="file"
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const url = await uploadResume(file);
                        if (url) setValue("resume", url);
                      }
                    }}
                  />
                  {watch("resume") && (
                    <a
                      href={watch("resume")}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-xs mt-1"
                    >
                      View Resume
                    </a>
                  )}
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="raviNote"
                  >
                    Ravi Note
                  </label>
                  <input
                    id="raviNote"
                    {...register("raviNote")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("raviNote") || ""}
                    onChange={(e) => setValue("raviNote", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="interviewDate"
                  >
                    Interview Date
                  </label>
                  <input
                    id="interviewDate"
                    type="date"
                    {...register("interviewDate")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={
                      watch("interviewDate")
                        ? watch("interviewDate").slice(0, 10)
                        : ""
                    }
                    onChange={(e) => setValue("interviewDate", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="interviewTime"
                  >
                    Interview Time
                  </label>
                  <input
                    id="interviewTime"
                    type="time"
                    {...register("interviewTime")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("interviewTime") || ""}
                    onChange={(e) => setValue("interviewTime", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="meetingLink"
                  >
                    Meeting Link
                  </label>
                  <input
                    id="meetingLink"
                    {...register("meetingLink")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("meetingLink") || ""}
                    onChange={(e) => setValue("meetingLink", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="lastIncrement"
                  >
                    Last Increment
                  </label>
                  <input
                    id="lastIncrement"
                    {...register("lastIncrement")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("lastIncrement") || ""}
                    onChange={(e) => setValue("lastIncrement", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="salaryBeforeLastIncrement"
                  >
                    Salary Before Last Increment
                  </label>
                  <input
                    id="salaryBeforeLastIncrement"
                    {...register("salaryBeforeLastIncrement")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("salaryBeforeLastIncrement") || ""}
                    onChange={(e) => setValue("salaryBeforeLastIncrement", e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="reasonForJobChange"
                  >
                    Reason For Job Change
                  </label>
                  <input
                    id="reasonForJobChange"
                    {...register("reasonForJobChange")}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    value={watch("reasonForJobChange") || ""}
                    onChange={(e) => setValue("reasonForJobChange", e.target.value)}
                  />
                </div>
              </div>
            </fieldset>
            <div className="flex justify-center mt-6 gap-4">
              <CancelButton
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-6 rounded-lg shadow transition"
                onClick={() => navigate(buildReturnURLWithCurrentValues())}
                children="Cancel"
                type="button"
              />
              <FormButton
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg shadow transition"
                type="submit"
                children="Save Changes"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditHiring;